import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileText, Loader2, Search, X } from 'lucide-react';

import {
  apiClient,
  type ApiError,
  type DocumentTemplate,
} from '@/lib/api-client';
import { cn } from '@/lib/utils';

interface SelecionarModeloModalProps {
  clientId: string;
  clientName: string;
  caseId?: string | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Modal que parte do contexto de **cliente** para escolher um modelo de
 * documento. Click no card dispara o POST `/documents/instances` direto
 * (sem botao "Gerar" separado) e abre o editor em nova aba. UX igual
 * "selecionar arquivo" no Mac/Windows: 1 click = ação.
 *
 * Convive com o `GerarDocumentoModal` (fluxo inverso, partindo do modelo).
 */
export function SelecionarModeloModal({
  clientId,
  clientName,
  caseId,
  open,
  onClose,
}: SelecionarModeloModalProps) {
  const [search, setSearch] = useState('');
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset state quando reabre.
  useEffect(() => {
    if (open) {
      setSearch('');
      setActiveTemplateId(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const templatesQuery = useQuery({
    queryKey: ['documents', 'templates', 'list', { status: 'active' }],
    queryFn: () => apiClient.listDocumentTemplates({ status: 'active' }),
    enabled: open,
    staleTime: 1000 * 60,
  });

  const items = templatesQuery.data ?? [];

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((t) => {
      const haystack = `${t.name} ${t.description ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [items, search]);

  const createMutation = useMutation({
    mutationFn: (templateId: string) =>
      apiClient.createDocumentInstance({
        templateId,
        clientId,
        caseId: caseId ?? null,
        title: null,
      }),
    onSuccess: (instance) => {
      onClose();
      window.open(
        `/escritorio/documentos/instancias/${instance.id}/editor`,
        '_blank',
        'noopener,noreferrer',
      );
    },
    onError: (err) => {
      setError((err as ApiError).message ?? 'Falha ao gerar documento');
      setActiveTemplateId(null);
    },
  });

  function handleSelectTemplate(template: DocumentTemplate) {
    if (createMutation.isPending) return;
    setError(null);
    setActiveTemplateId(template.id);
    createMutation.mutate(template.id);
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="selecionar-modelo-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => {
          if (!createMutation.isPending) onClose();
        }}
        aria-hidden
      />
      <div
        className={cn(
          'relative z-[61] flex w-full max-w-4xl flex-col overflow-hidden rounded-lg border shadow-xl',
          'border-[var(--modal-border,var(--card-border))] bg-[var(--modal-bg,var(--card-bg))] backdrop-blur-[var(--blur)]',
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-[var(--border)] px-5 py-4">
          <div className="min-w-0">
            <h2
              id="selecionar-modelo-modal-title"
              className="truncate text-base font-semibold text-[var(--fg)]"
            >
              Gerar documento para "{clientName}"
            </h2>
            <p className="mt-1 text-xs text-[var(--fg-muted)]">
              Selecione um modelo para gerar o documento. As variáveis do
              cliente serão preenchidas automaticamente.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            aria-label="Fechar"
            className="rounded-md p-1 text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)] disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex flex-col gap-4 px-5 py-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--fg-muted)]" />
            <input
              type="search"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar modelo por nome..."
              className="h-9 w-full rounded-md border border-[var(--border)] bg-[var(--surface)] pl-8 pr-2 text-sm text-[var(--fg)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            />
          </div>

          {error && (
            <p className="text-xs text-red-500" role="alert">
              {error}
            </p>
          )}

          <div className="max-h-[60vh] overflow-y-auto">
            {templatesQuery.isLoading ? (
              <ModelosGridSkeleton />
            ) : filteredItems.length === 0 ? (
              <EmptyModelos hasSearch={search.length > 0} />
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {filteredItems.map((tpl) => (
                  <ModeloCardCompact
                    key={tpl.id}
                    template={tpl}
                    isLoading={
                      createMutation.isPending && activeTemplateId === tpl.id
                    }
                    disabled={createMutation.isPending}
                    onClick={() => handleSelectTemplate(tpl)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] px-5 py-3">
          <p className="text-[11px] text-[var(--fg-muted)]">
            Click em um modelo para gerar o documento e abrir o editor em nova
            aba.
          </p>
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            className="rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--fg)] hover:bg-[var(--surface-muted)] disabled:opacity-50"
          >
            Cancelar
          </button>
        </footer>
      </div>
    </div>
  );
}

function ModeloCardCompact({
  template,
  isLoading,
  disabled,
  onClick,
}: {
  template: DocumentTemplate;
  isLoading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group relative flex cursor-pointer flex-col overflow-hidden rounded-lg border text-left transition',
        'border-[var(--card-border)] bg-[var(--card-bg)] hover:shadow-md hover:border-[var(--accent)]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
        disabled && !isLoading && 'cursor-not-allowed opacity-50',
        isLoading && 'border-[var(--accent)]',
      )}
    >
      <div className="relative flex h-24 items-center justify-center border-b border-[var(--border)] bg-white">
        <FileText className="h-9 w-9 text-slate-300" strokeWidth={1.2} />
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--accent)]" />
          </div>
        )}
      </div>
      <div className="p-2.5">
        <h3 className="truncate text-xs font-medium text-[var(--fg)]">
          {template.name}
        </h3>
        {template.description && (
          <p className="mt-0.5 truncate text-[10px] text-[var(--fg-muted)]">
            {template.description}
          </p>
        )}
      </div>
    </button>
  );
}

function ModelosGridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)]"
        >
          <div className="h-24 animate-pulse bg-[var(--surface-muted)]" />
          <div className="p-2.5">
            <div className="h-3 w-3/4 animate-pulse rounded bg-[var(--surface-muted)]" />
            <div className="mt-1.5 h-2 w-1/2 animate-pulse rounded bg-[var(--surface-muted)]" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyModelos({ hasSearch }: { hasSearch: boolean }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-[var(--border)] px-4 py-12 text-center text-xs text-[var(--fg-muted)]">
      <FileText className="h-8 w-8 opacity-40" />
      {hasSearch
        ? 'Nenhum modelo corresponde a busca.'
        : 'Você ainda não tem modelos cadastrados.'}
    </div>
  );
}
